import { useSnackbar } from 'notistack';
import { useCallback, useMemo } from 'react';
import useAuthStore from '@pkg/auth/useStore';
import useDatabase from '@pkg/database/useDatabase';
import * as Obj from '@pkg/utils/objects';
import get from '@pkg/utils/objects/get';
import deriveProps from './deriveProps';
import getOrdered from './get/ordered';
import useMutateTagging from './mutations/useMutateTagging';
import * as Reducers from './reducers';
import useState from './useState';
import useStore from './useStore';

/**
 * @returns {Array}
 */
export default function useTags() {
  const { organisation } = useAuthStore();
  const tags = useStore((state) => state.tags);
  const categories = useStore((state) => state.categories);
  const database = useDatabase();
  const { enqueueSnackbar } = useSnackbar();

  const [state, mutateState, setState] = useState(tags, categories);
  const mutateTagging = useMutateTagging();

  const handler = async (action, input) => {
    const mutation = get(Reducers, action)(state, input);
    if (!mutation) {
      return;
    }

    // Optimistically update local state
    mutateState(mutation);

    try {
      const response = await mutateTagging(organisation.uuid, mutation);

      // Server is source of truth - update database with its response
      // useLive() will detect this change and propagate it through:
      // IndexedDB → Global Store (useStore) → Local State (useLocalState)
      if (response?.data?.mutateTagging) {
        const serverTags = response.data.mutateTagging.tags;
        const serverCategories = response.data.mutateTagging.tagCategories;

        await database.transaction(
          'rw',
          ['tags', 'tagCategories'],
          async () => {
            // Clear and replace with server data to ensure sync
            await database.tags.clear();
            await database.tags.bulkPut(serverTags);

            await database.tagCategories.clear();
            await database.tagCategories.bulkPut(serverCategories);
          }
        );
      }
    } catch (error) {
      console.error('Tag mutation failed:', error);

      enqueueSnackbar(
        'Failed to update tags. Your changes have been reverted.',
        {
          variant: 'error',
        }
      );

      // Rollback optimistic update by restoring from global store
      // (which still has the pre-mutation data since we didn't update IndexedDB)
      setState({ tags, categories });
    }
  };

  const onDispatch = useCallback(handler, [
    mutateState,
    mutateTagging,
    state,
    tags,
    categories,
  ]);

  return useMemo(() => {
    const raw = Obj.omitDerived({
      categories: state.categories.list,
      tags: state.tags.list,
    });

    const { tags, categories } = deriveProps(raw.tags, raw.categories);
    const ordered = getOrdered(tags, categories);
    return { onDispatch, ordered };
  }, [onDispatch, state]);
}
